import { ComponentPropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { Caption, CaptionProps } from '../Caption/Caption';
import { InputLabelText } from '../Input/components/InputLabelText';
import { Optional } from '../Input/components/Optional';
import { Required } from '../Input/components/Required';
import { Text } from '../Text/Text';

export interface TextAreaProps extends ComponentPropsWithoutRef<'textarea'> {
    /**
     * Define a label shown above the textarea
     */
    label?: string;
    caption?: string;
    CaptionIcon?: CaptionProps['Icon'];
    negative?: boolean;
    /**
     * Add required indicator to label
     */
    markAsRequired?: boolean;
    /**
     * Add optional indicator to label
     */
    markAsOptional?: boolean;
    /**
     * Add a description below the label
     */
    description?: string;
}

const TextAreaLabel = styled.label`
    color: ${theme.colors.foreground.muted};
    display: inline-flex;
    flex-direction: column;
    width: 100%;

    // required to override global styles
    margin-bottom: 0;
    font-weight: ${theme.fontWeights[500]};
`;

interface BaseTextAreaProps extends ComponentPropsWithoutRef<'textarea'> {
    negative?: boolean;
}

const BaseTextArea = styled.textarea<BaseTextAreaProps>`
    resize: none;
    font-size: ${theme.fontSizes[14]};
    font-weight: ${theme.fontWeights[400]};
    background-color: ${(props) => (props.disabled ? theme.colors.background.subtle : theme.colors.background.default)};
    color: ${(props) => (props.disabled ? theme.colors.foreground.disabled : theme.colors.foreground.default)};
    border-radius: 6px;
    width: 100%;
    line-height: 24px;
    padding: ${theme.space[4]} ${theme.space[8]};
    margin: 0;
    transition: box-shadow 75ms;
    border: none;
    box-shadow: ${theme.shadows.border.default};

    &:focus {
        outline: none;
        box-shadow: ${(props) =>
            props.negative
                ? `0px 0px 0px 1px ${theme.colors.negative.foreground}, 0px 0px 0px 3px ${theme.colors.effects.focus}`
                : `0px 0px 0px 3px ${theme.colors.effects.focus}, 0px 0px 0px 1px ${theme.colors.border.muted}`};
    }

    &:disabled {
        box-shadow: ${theme.shadows.border.disabled};
    }

    ${(props) =>
        props.negative
            ? css`
                  box-shadow: ${theme.shadows.border.negative};
              `
            : null}

    &::placeholder {
        color: ${theme.colors.foreground.subtle};
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 60px transparent inset !important;
        background-color: transparent !important;
        background-clip: content-box !important;
    }
`;

export const TextArea = ({
    label,
    caption,
    CaptionIcon,
    disabled,
    negative,
    markAsRequired,
    markAsOptional,
    description,
    ...rest
}: TextAreaProps) => {
    return (
        <TextAreaLabel>
            <Box display="flex" flexDirection="column" gap={4}>
                {label ? (
                    <InputLabelText disabled={disabled}>
                        {label}
                        {markAsRequired ? <Required disabled={disabled} /> : null}
                        {markAsOptional ? <Optional disabled={disabled} /> : null}
                    </InputLabelText>
                ) : null}

                {description ? (
                    <Text variant="extraSmall" color="foreground.muted">
                        {description}
                    </Text>
                ) : null}

                <BaseTextArea {...rest} disabled={disabled} negative={negative} />

                {caption ? (
                    <Caption $negative={negative} Icon={CaptionIcon}>
                        {caption}
                    </Caption>
                ) : null}
            </Box>
        </TextAreaLabel>
    );
};
